import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { titleCaseSentence, upperSnakeCase } from '@/utils';
import { POI_DETAILS_DEFAULT, POI_DETAILS_FALLBACK } from '@/constants';
import { LegendColors } from '@/types';
import { KeyValueMap, mapFindingsData } from './../utils';
import {
  PointOfInterestsFindingsByDeckAndPaintBlock,
  useGetAllPointOfInterestTemplatesQuery,
} from '@/__generated__/graphql';

type FindingsChartData = {
  findingsCountByDeck: KeyValueMap[];
  findingsCountByPaintBlock: KeyValueMap[];
};

export const useFindingsData = (
  findingsByPaintBlocksAndDecks: PointOfInterestsFindingsByDeckAndPaintBlock | undefined,
  selectedPOIType: string[] = [],
  activeStructureIds: string[] = []
) => {
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();

  const { data: fetchedTemplates } = useGetAllPointOfInterestTemplatesQuery({
    variables: {
      structureId: activeStructureIds[0],
      assemblyTemplateId: selectedAssemblyTemplateId || '',
    },
    fetchPolicy: 'no-cache',
    skip:
      !activeStructureIds[0] ||
      (allPointOfInterestTemplates.length > 0 && activeStructureIds.length > 0),
  });

  const pointOfInterestTemplates = useMemo(
    () =>
      allPointOfInterestTemplates.length > 0
        ? allPointOfInterestTemplates
        : fetchedTemplates?.allPointOfInterestTemplate || [],
    [allPointOfInterestTemplates, fetchedTemplates]
  );

  const updateFindings = useCallback(
    (data: KeyValueMap[]) => {
      return data.map((poiData) => {
        Object.keys(poiData).forEach((key) => {
          if (
            key !== 'name' &&
            !selectedPOIType.some((poi) => poi.toLowerCase().includes(key.toLowerCase()))
          ) {
            poiData[key] = 0;
          }
        });
        return poiData;
      });
    },
    [selectedPOIType]
  );

  const chartsData: FindingsChartData = useMemo(() => {
    if (selectedPOIType.length === 0 && findingsByPaintBlocksAndDecks) {
      return { ...mapFindingsData({ ...findingsByPaintBlocksAndDecks }) };
    } else if (findingsByPaintBlocksAndDecks) {
      const mappedDataClone = { ...mapFindingsData({ ...findingsByPaintBlocksAndDecks }) };
      return {
        findingsCountByDeck: updateFindings(mappedDataClone.findingsCountByDeck),
        findingsCountByPaintBlock: updateFindings(mappedDataClone.findingsCountByPaintBlock),
      };
    }

    return { findingsCountByDeck: [], findingsCountByPaintBlock: [] };
  }, [findingsByPaintBlocksAndDecks, selectedPOIType.length, updateFindings]);

  const findingsLegend = useMemo(() => {
    return pointOfInterestTemplates.reduce((accumulator: LegendColors, current) => {
      const currentKey = titleCaseSentence(current?.key);
      const templateColor = pointOfInterestTemplates.find(
        (template) => upperSnakeCase(template?.key) === upperSnakeCase(currentKey)
      )?.accentColor;
      accumulator[current.name] =
        templateColor ??
        POI_DETAILS_FALLBACK.find(
          (fallbackDetail) => fallbackDetail.name === upperSnakeCase(currentKey)
        )?.accentColor ??
        POI_DETAILS_DEFAULT.accentColor;
      return accumulator;
    }, {});
  }, [pointOfInterestTemplates]);

  return {
    chartsData,
    findingsLegend,
    pointOfInterestTemplates,
  };
};
